import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { TextLink } from "./kit/TextLink";
import { EyeOff, Send, Trash2, BookOpen, ChevronLeft } from "lucide-react";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from "./ui/select";
import { SectionStrip, SectionPanel } from "./kit";
import { coachPlayersName } from "../lib/coachPlayersName";
import PlayerProfileView from "./PlayerProfileView";
import PlayerHealthPanel from "./PlayerHealthPanel";
import CoachFullRecordRow from "./CoachFullRecordRow";
import CoachGoalsPanel from "./CoachGoalsPanel";
import CoachFeedbackPanel from "./CoachFeedbackPanel";
import CoachMatchReflectionsPanel from "./CoachMatchReflectionsPanel";
import CoachReflectionsPanel from "./CoachReflectionsPanel";
import CoachDrillsPanel from "./CoachDrillsPanel";
import PlayerIdentityPanel from "./PlayerIdentityPanel";
import LoadFailureNote from "./LoadFailureNote";
import { SectionCard } from "./identityForm";
import AssignGoalDialog from "./AssignGoalDialog";

// #106's phone touch-target idiom — the same pair `CoachPortalShortcuts.jsx`
// and every other control on this page already carries.
const TOUCH = "h-11 sm:h-9";

// #1221 slice 1 (D240, D251): the coach's view of a player is three sections.
// The testids are the old tab testids, kept so existing readers keep working.
const SECTIONS = [
  { key: "goals", label: "Goals", testId: "cp-tab-goals" },
  { key: "feedback", label: "Feedback", testId: "cp-tab-notes" },
  { key: "match-reflections", label: "Reflections", testId: "cp-tab-match-reflections" },
];

/**
 * The section an incoming `?tab=` value belongs to. Old values keep working:
 * `identity` and `goals` are Goals; `feedback` and `reflections` (the coach's
 * own private notes) are Feedback; `match-reflections` is Reflections;
 * `guides` is on no section (D254 Q1); anything else is Goals.
 */
export function sectionForTab(tab) {
  if (tab === "guides") return null;
  if (tab === "feedback" || tab === "reflections") return "feedback";
  if (tab === "match-reflections") return "match-reflections";
  return "goals";
}

const HALF_BASE = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 6,
  flex: "1 1 0",
  minWidth: 0,
  minHeight: "var(--nw-size-tap)",
  padding: "8px 12px",
  border: 0,
  borderRadius: 8,
  fontSize: 15,
  cursor: "pointer",
};

/** The Feedback section's two-part switch: not a place to go (D224), so it is
 *  two `aria-pressed` buttons over local state, and it never writes `?tab=`. */
function FeedbackHalfSwitch({ half, onChange, firstName }) {
  const items = [
    { key: "feedback", label: `Feedback to ${firstName}`, testId: "cp-feedback-half-feedback" },
    { key: "private", label: "Private notes", testId: "cp-feedback-half-private", icon: EyeOff },
  ];
  return (
    <div
      className="flex gap-0.5 p-1 mb-4"
      style={{ background: "var(--nw-line-faint)", borderRadius: "var(--nw-radius-control)" }}
      data-testid="cp-feedback-halves"
    >
      {items.map((it) => {
        const on = half === it.key;
        return (
          <button
            key={it.key}
            type="button"
            aria-pressed={on ? "true" : "false"}
            data-testid={it.testId}
            onClick={() => onChange(it.key)}
            className="break-words"
            style={{
              ...HALF_BASE,
              background: on ? "var(--nw-surface)" : "transparent",
              color: on ? "var(--nw-ink)" : "var(--nw-ink-muted)",
              fontWeight: on ? 600 : 500,
              boxShadow: on ? "0 1px 2px rgba(15,23,42,0.12)" : "none",
            }}
          >
            {it.icon && <it.icon size={15} aria-hidden="true" style={{ flexShrink: 0 }} />}
            <span style={{ minWidth: 0, overflowWrap: "anywhere" }}>{it.label}</span>
          </button>
        );
      })}
    </div>
  );
}

/**
 * Issue #973 (D186) — the coach's one-player page, behind `coach-player`.
 *
 * `CoachPortal.jsx` renders this in place of its own whole shipped page
 * (an early return, before its own `return`) once the flag is on, the
 * viewer is a coach, and `?player=<id>` names a roster player — see that
 * file's own comment on the call site for the D186.2 not-found card and
 * the D186 "loading" shell, which are NOT this component's job: by the
 * time this mounts, the caller has already confirmed the id is on the
 * roster, and `key={player.player_id}` on that call site is what makes a
 * player switch a full remount of everything below (#431 — the same
 * cross-player safety `CoachCareerSnapshot.jsx`'s own header explains).
 *
 * TOP TO BOTTOM, AS DRAWN (`CoachPlayer.dc.html`, D88's trim of D53):
 * Back, the header (eyebrow + name + role·club), the profile card, the
 * health card (navy rule, no gold — D72 via `PlayerHealthPanel`'s own
 * `variant="coach-player"`), the folded career snapshot, the reading
 * notice and the match cards (folded from `CoachPlayerRecord.jsx`, D86).
 * BELOW all of that: #1221 slice 1 (D240, D251) replaced D186.1's shortcut
 * ribbon and six-tab strip with the kit's `SectionStrip` of three sections,
 * Goals · Feedback · Reflections. Every prop below is the identical state and
 * handler `CoachPortal.jsx` already owns, handed down instead of closed over;
 * the flag-off page and admin keep that file's own six tabs (D228.2).
 */
export default function CoachPlayerScreen({
  player,
  viewerRole,
  // #1430: the squad is beside this page, so the way back to it is not drawn.
  besideSquad = false,
  goals,
  goalSort,
  onGoalSortChange,
  editingGoal,
  onBeginEditGoal,
  onEditingGoalChange,
  onSaveGoalProgress,
  onCancelEditGoal,
  onRemoveGoal,
  goalsStatus = "loaded",
  onRetryGoals,
  onOpenGoalDialog,
  notes,
  noteSort,
  onNoteSortChange,
  filterCat,
  onFilterCatChange,
  categories,
  notesStatus = "loaded",
  onRetryNotes,
  canMutateNote,
  onTogglePinNote,
  onDeleteNote,
  feedback,
  onFeedbackChange,
  category,
  onCategoryChange,
  onAddNote,
  feedbackFocus,
  onFeedbackFocusHandled,
  guideCatalogue,
  catalogueStatus,
  onRetryCatalogue,
  assignedGuides,
  guideToAssign,
  onGuideToAssignChange,
  assigningGuide,
  onAssignGuide,
  onRemoveAssignedGuide,
  matchReflections,
  matchReflectionsStatus = "loaded",
  onRetryMatchReflections,
  matchReflectionSort,
  onMatchReflectionSortChange,
  reflections,
  reflectionsStatus = "loaded",
  onRetryReflections,
  reflectionSort,
  onReflectionSortChange,
  isCoach,
  editingReflection,
  onEditingReflectionChange,
  onBeginEditReflection,
  onSaveReflectionEdit,
  onDeleteReflection,
  reflectionBody,
  onReflectionBodyChange,
  onAddReflection,
  tab,
  onGoToTab,
  goalDialog,
  onGoalDialogChange,
  newGoal,
  onNewGoalChange,
  onSaveGoal,
}) {
  const playerId = player.player_id;
  const playerName = player.full_name;
  const roleClub = [player.playing_role, player.club].filter(Boolean).join(" · ");
  const firstName = playerName.split(" ")[0];
  const section = sectionForTab(tab);
  // The Feedback section's two-part switch (#1221 slice 3, D224): local state,
  // seeded from the incoming tab, and re-seeded when a link changes the tab on
  // this page. The composers' text is the page's, so a switch loses nothing.
  const [half, setHalf] = useState(tab === "reflections" ? "private" : "feedback");
  useEffect(() => {
    if (tab === "reflections") setHalf("private");
    else if (tab === "feedback") setHalf("feedback");
  }, [tab]);
  // #1221 slice 2: "Goal assigned to [name]" (the full name, like the sheet title) stays on the Goals section until
  // the next New goal, or until the coach leaves the section (D48: a line that
  // stays, not a toast).
  const [assignedNotice, setAssignedNotice] = useState(null);
  useEffect(() => {
    setAssignedNotice(null);
  }, [section]);
  const openGoalDialog = () => {
    setAssignedNotice(null);
    onOpenGoalDialog();
  };
  const assignGoal = async () => {
    await onSaveGoal();
    setAssignedNotice(`Goal assigned to ${playerName}`);
  };

  return (
    <div className="space-y-6 min-w-0" data-testid="coach-portal-page">
      {/* #1221 slice 1 (D240, D251): three sections. The ribbon of shortcuts
          (`CoachPortalShortcuts`) is not drawn on this page: New goal lives in
          Goals and Add feedback in Feedback. The strip is the kit's
          `SectionStrip`; the testids are the old tab ones. D311: it sits
          directly under the ribbon, ahead of the back link and header. */}
      <SectionStrip
        data-testid="cp-sections"
        idPrefix="cp-sections"
        items={SECTIONS}
        currentKey={section}
        onChange={onGoToTab}
      />

      {!besideSquad && (
        <TextLink
          as={Link}
          to="/coach-portal"
          className="gap-1.5 text-sm font-semibold text-[#0B2E59]"
          data-testid="cp-player-back"
          // Second child of the stack (the strip is first, D311): the inline
          // `marginBlock` of the tap style beats `space-y-6`, so both gaps are set
          // here (`tapTarget.js` "Placement trap"). The board's 14px is the column
          // gap between the strip and this link's 44px tap box, and again between
          // the box and the eyebrow (D259), not the label. A block-level box with
          // no bottom pull-back makes the 44px box its own footprint, so the
          // eyebrow's 14px below is measured from the box and not from a line.
          style={{ display: "flex", width: "fit-content", marginTop: 14, marginBottom: 0 }}
        >
          <ChevronLeft size={16} aria-hidden="true" />
          {coachPlayersName()}
        </TextLink>
      )}

      <div className="flex flex-col gap-1 min-w-0" style={{ marginTop: 14 }}>
        <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500">{coachPlayersName()}</p>
        <h1
          className="font-display font-black text-2xl text-slate-900 break-words"
          data-testid="cp-selected-name"
        >
          {playerName}
        </h1>
        {roleClub && <p className="text-sm text-slate-500">{roleClub}</p>}
      </div>

      {/* #1221 slice 5 (D214 L7, D298, D301): the way into the full record. One
          instance, outside every section panel, so it shows on all three. */}
      <CoachFullRecordRow playerId={playerId} />

      <PlayerProfileView player={player} variant="coach-player" testId="cp-player-profile" />

      <PlayerHealthPanel playerId={playerId} playerName={playerName} viewerRole={viewerRole} variant="coach-player" />

      <div className="space-y-4">
        {section === "goals" && (
          <SectionPanel idPrefix="cp-sections" currentKey="goals" className="space-y-4">
          <PlayerIdentityPanel playerId={playerId} playerName={playerName} viewerRole={viewerRole} variant="coach-player" />
          <CoachGoalsPanel
            appearance="r5"
            goals={goals}
            status={goalsStatus}
            onRetry={onRetryGoals}
            sort={goalSort}
            onSortChange={onGoalSortChange}
            editingGoal={editingGoal}
            onBeginEdit={onBeginEditGoal}
            onEditingGoalChange={onEditingGoalChange}
            onSaveProgress={onSaveGoalProgress}
            onCancelEdit={onCancelEditGoal}
            onRemove={onRemoveGoal}
            onNewGoal={openGoalDialog}
            notice={assignedNotice}
          />
          </SectionPanel>
        )}

        {section === "feedback" && (
          <SectionPanel idPrefix="cp-sections" currentKey="feedback">
          <FeedbackHalfSwitch half={half} onChange={setHalf} firstName={firstName} />
          {half === "feedback" ? (
          <CoachFeedbackPanel
            appearance="r5"
            status={notesStatus}
            onRetry={onRetryNotes}
            firstName={firstName}
            notes={notes}
            sort={noteSort}
            onSortChange={onNoteSortChange}
            filterCat={filterCat}
            onFilterChange={onFilterCatChange}
            categories={categories}
            canMutateNote={canMutateNote}
            onTogglePin={onTogglePinNote}
            onRemove={onDeleteNote}
            feedback={feedback}
            onFeedbackChange={onFeedbackChange}
            category={category}
            onCategoryChange={onCategoryChange}
            onAdd={onAddNote}
            focusNonce={feedbackFocus}
            onFocusHandled={onFeedbackFocusHandled}
          />
          ) : (
          <CoachReflectionsPanel
            appearance="r5"
            status={reflectionsStatus}
            onRetry={onRetryReflections}
            reflections={reflections}
            sort={reflectionSort}
            onSortChange={onReflectionSortChange}
            isCoach={isCoach}
            firstName={firstName}
            editing={editingReflection}
            onEditingChange={onEditingReflectionChange}
            onBeginEdit={onBeginEditReflection}
            onSaveEdit={onSaveReflectionEdit}
            onRemove={onDeleteReflection}
            body={reflectionBody}
            onBodyChange={onReflectionBodyChange}
            onAdd={onAddReflection}
          />
          )}
          </SectionPanel>
        )}

        {/* D254 Q1: reachable, off the strip, until #1159 (assign moves to Training & guides) */}
        {section === null && (
          <div className="space-y-4">
            <SectionCard
              appearance="r5"
              key={`guide-assign-${playerId}`}
              icon={BookOpen}
              title="Assign a guide"
              desc={`${playerName} sees it on Training & Guides, with your name on it.`}
              collapsible
              defaultOpen={false}
              summary={catalogueStatus === "loaded" ? `${guideCatalogue.length} published` : "—"}
              toggleTestid="cp-guide-assign-toggle"
              testid="cp-guide-assign-card"
            >
              <div className="flex flex-col sm:flex-row gap-2">
                <Select value={guideToAssign} onValueChange={onGuideToAssignChange}>
                  <SelectTrigger className={`flex-1 ${TOUCH}`} data-testid="cp-guide-select">
                    <SelectValue placeholder="Choose a published guide" />
                  </SelectTrigger>
                  <SelectContent position="item-aligned">
                    {[...new Set(guideCatalogue.map((g) => g.category))].map((cat) => (
                      <SelectGroup key={cat} data-testid={`cp-guide-group-${cat}`}>
                        <SelectLabel className="capitalize">{cat.replace(/-/g, " ")}</SelectLabel>
                        {guideCatalogue.filter((g) => g.category === cat).map((g) => (
                          <SelectItem key={`${g.category}/${g.slug}`} value={`${g.category}/${g.slug}`}>
                            {g.category.replace(/-/g, " ")} · {g.title}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  onClick={onAssignGuide}
                  disabled={!guideToAssign || assigningGuide}
                  className={TOUCH}
                  style={{ background: "#0B2E59" }}
                  data-testid="cp-assign-guide-btn"
                >
                  <Send size={14} className="mr-1.5" /> {assigningGuide ? "Assigning…" : "Assign"}
                </Button>
              </div>

              {catalogueStatus === "failed" && (
                <LoadFailureNote what="the guide catalogue" testId="cp-guide-catalogue-error" onRetry={onRetryCatalogue} />
              )}

              {catalogueStatus === "loaded" && guideCatalogue.length === 0 && (
                <p className="text-xs text-slate-500" data-testid="cp-no-published-guides">
                  No guides are published yet. A guide can only be assigned once it has been reviewed.
                </p>
              )}
            </SectionCard>

            <SectionCard
              appearance="r5"
              key={`guide-assigned-${playerId}`}
              icon={BookOpen}
              title="Already assigned"
              desc={`The guides ${playerName.split(" ")[0]} already has, and who assigned each.`}
              collapsible
              defaultOpen={false}
              count={assignedGuides.length}
              toggleTestid="cp-assigned-toggle"
              testid="cp-assigned-guides-card"
            >
              <div className="space-y-2" data-testid="cp-assigned-guides">
                {assignedGuides.length === 0 && <p className="text-sm text-slate-500">Nothing assigned yet.</p>}
                {assignedGuides.map((a) => (
                  <div
                    key={a.assignment_id}
                    className="rounded-xl border border-slate-200 p-3 flex items-center gap-3"
                    data-testid={`cp-assigned-${a.category}-${a.slug}`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-sm text-slate-900">{a.title}</div>
                      <div className="text-xs text-slate-500 mt-0.5">Assigned by {a.assigned_by_coach_name}</div>
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => onRemoveAssignedGuide(a)}
                      aria-label={`Remove ${a.title}`}
                      data-testid={`cp-unassign-${a.category}-${a.slug}`}
                    >
                      <Trash2 size={14} className="text-slate-400" />
                    </Button>
                  </div>
                ))}
              </div>
            </SectionCard>

            <CoachDrillsPanel playerId={playerId} playerName={playerName} appearance="r5" />
          </div>
        )}

        {section === "match-reflections" && (
          <SectionPanel idPrefix="cp-sections" currentKey="match-reflections">
          <CoachMatchReflectionsPanel
            appearance="r5"
            status={matchReflectionsStatus}
            onRetry={onRetryMatchReflections}
            reflections={matchReflections}
            sort={matchReflectionSort}
            onSortChange={onMatchReflectionSortChange}
            firstName={firstName}
          />
          </SectionPanel>
        )}
      </div>

      <AssignGoalDialog
        appearance="r5"
        open={goalDialog}
        onOpenChange={onGoalDialogChange}
        playerName={playerName}
        goal={newGoal}
        onGoalChange={onNewGoalChange}
        onSave={assignGoal}
      />
    </div>
  );
}
